import React, { createContext, useContext, useReducer, ReactNode } from 'react';
import { Lesson, LessonSession } from '../types';

interface LessonState {
  lessons: Lesson[];
  currentLesson: Lesson | null;
  currentSession: LessonSession | null;
  loading: boolean;
  error: string | null;
}

type LessonAction =
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_ERROR'; payload: string | null }
  | { type: 'SET_LESSONS'; payload: Lesson[] }
  | { type: 'SET_CURRENT_LESSON'; payload: Lesson | null }
  | { type: 'SET_CURRENT_SESSION'; payload: LessonSession | null }
  | { type: 'ADD_LESSON'; payload: Lesson }
  | { type: 'UPDATE_LESSON'; payload: Lesson }
  | { type: 'DELETE_LESSON'; payload: string }
  | { type: 'UPDATE_SESSION'; payload: Partial<LessonSession> };

const initialState: LessonState = {
  lessons: [],
  currentLesson: null,
  currentSession: null,
  loading: false,
  error: null,
};

const lessonReducer = (state: LessonState, action: LessonAction): LessonState => {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, loading: action.payload };
    case 'SET_ERROR':
      return { ...state, error: action.payload };
    case 'SET_LESSONS':
      return { ...state, lessons: action.payload };
    case 'SET_CURRENT_LESSON':
      return { ...state, currentLesson: action.payload };
    case 'SET_CURRENT_SESSION':
      return { ...state, currentSession: action.payload };
    case 'ADD_LESSON':
      return { ...state, lessons: [action.payload, ...state.lessons] };
    case 'UPDATE_LESSON':
      return {
        ...state,
        lessons: state.lessons.map(lesson =>
          lesson.metadata.lesson_id === action.payload.metadata.lesson_id ? action.payload : lesson
        ),
        currentLesson: state.currentLesson?.metadata.lesson_id === action.payload.metadata.lesson_id ? action.payload : state.currentLesson,
      };
    case 'DELETE_LESSON':
      return {
        ...state,
        lessons: state.lessons.filter(lesson => lesson.metadata.lesson_id !== action.payload),
        currentLesson: state.currentLesson?.metadata.lesson_id === action.payload ? null : state.currentLesson,
      };
    case 'UPDATE_SESSION':
      return {
        ...state,
        currentSession: state.currentSession
          ? { ...state.currentSession, ...action.payload }
          : null,
      };
    default:
      return state;
  }
};

interface LessonContextType {
  state: LessonState;
  dispatch: React.Dispatch<LessonAction>;
}

const LessonContext = createContext<LessonContextType | undefined>(undefined);

export const useLessonContext = () => {
  const context = useContext(LessonContext);
  if (context === undefined) {
    throw new Error('useLessonContext must be used within a LessonProvider');
  }
  return context;
};

interface LessonProviderProps {
  children: ReactNode;
}

export const LessonProvider: React.FC<LessonProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(lessonReducer, initialState);

  return (
    <LessonContext.Provider value={{ state, dispatch }}>
      {children}
    </LessonContext.Provider>
  );
};
